/**
 * useFunnelAddToCart Hook
 *
 * Reusable hook that provides a funnel-aware add-to-cart callback.
 * Handles SKU/variant resolution for funnel offers and integrates
 * with the storefront cart system.
 *
 * Used by FunnelExperience on all page types (PDP, homepage, collection).
 */

'use client';

import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useAddToCart } from './use-product';
import { useCartContext } from '@/lib/context/cart-context';
import { useIntl } from '@/lib/intl';
import { productService } from '@/infrastructure/config/services.config';
import { storefrontLayoutLogger } from '@/lib/logger';
import {
  useFunnelContext,
  useFunnelStepEmitters,
  readAssignedFunnelId,
  isFunnelUiEventsDebugEnabled,
  type FunnelAddToCartCallback,
  type FunnelCartAuthorization,
} from '@template-project/storefront-funnel';
import {
  buildBrowserBusinessEventId,
  EVENT_TYPES,
  trackAddToCart,
  trackAddToCartFromLines,
} from '@/lib/analytics';
import { currencyRatioFromAmounts } from '@/domain/value-objects/currency-amount.vo';
import { toFunnelCartPageContext } from '@/lib/funnel-cart-context';

/** Funnel QA query flags preserved when ATC navigates to the full cart page. */
const FUNNEL_CART_NAV_QUERY_KEYS = [
  'funnel_ui_events_debug',
  'funnel_secure_cart_proof',
  'funnel_ui_events',
  'funnel_ui_events_transport',
] as const;

/**
 * Build `/cart` (optionally with funnel QA flags from the current search).
 * Used when debug mode skips SideCart so the cart-step page experience mounts.
 */
export function buildCartPagePath(search: string = ''): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const current = new URLSearchParams(raw);
  const next = new URLSearchParams();
  for (const key of FUNNEL_CART_NAV_QUERY_KEYS) {
    const value = current.get(key);
    if (value !== null && value !== '') next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `/cart?${qs}` : '/cart';
}

interface UseFunnelAddToCartOptions {
  /** Override country code (e.g. from PDP props). Falls back to intl config. */
  countryCode?: string;
  /** Override currency (e.g. from PDP props). Falls back to intl config. */
  currency?: string;
  vendor?: string;
  shop?: string;
}

/**
 * Returns a callback compatible with FunnelExperience's onAddToCart prop.
 *
 * The callback resolves product/variant IDs (which may be SKUs from the funnel offer)
 * into real UUIDs, then adds the item to the cart.
 */
export function useFunnelAddToCart(options: UseFunnelAddToCartOptions = {}) {
  const addToCartMutation = useAddToCart();
  const { cartToken, setCartToken, showCart } = useCartContext();
  const navigate = useNavigate();
  const { countryConfig } = useIntl();
  const { assignedFunnelId, activeExperience } = useFunnelContext();
  const { emitCartUpdated } = useFunnelStepEmitters();

  const countryCode = options.countryCode || countryConfig.countryCode;
  const currency = options.currency || countryConfig.currency.code;
  const vendor = options.vendor || 'shapermint';
  const shop = options.shop || 'shapermint';

  const persistedFunnelId =
    assignedFunnelId ?? activeExperience?.funnelId ?? readAssignedFunnelId() ?? null;
  const persistedFirstStepId = activeExperience?.stepId ?? null;

  const handleFunnelAddToCart = useCallback(
    async (
      productId: string,
      variantId: string,
      quantity: number,
      item?: Parameters<FunnelAddToCartCallback>[3],
      authorization?: FunnelCartAuthorization
    ) => {
      const subscription = item?.subscription;
      try {
        let resolvedProductId = productId;
        let resolvedVariantId = variantId;
        let offerProduct: Awaited<ReturnType<typeof productService.getProductById>> | null = null;

        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const productIsUuid = UUID_RE.test(productId);
        const variantIsUuid = UUID_RE.test(variantId);

        // Resolve the catalog entity even when both IDs are already UUIDs. The
        // cart mutation can use those IDs directly, but the golden + canonical
        // business events require the authoritative product name, SKU and price.
        // A failed enrichment lookup must never block a valid cart mutation.
        const pricingOpts = { currency, country: countryCode };

        offerProduct = productIsUuid
          ? await productService.getProductById(productId, pricingOpts).catch(() => null)
          : await productService.getProductBySlug(productId, pricingOpts).catch(() => null);
        if (offerProduct) {
          if (!variantIsUuid || !productIsUuid) {
            resolvedProductId = offerProduct.uuid ?? offerProduct.id ?? resolvedProductId;
            resolvedVariantId =
              offerProduct.variants.find((v) => v.enabled)?.id ||
              offerProduct.variants[0]?.id ||
              resolvedVariantId;
          }
        }

        const resolvedVariant = offerProduct?.variants.find((v) => v.id === resolvedVariantId);
        const unitPrice = resolvedVariant?.price ?? offerProduct?.price;
        const currencyRatio =
          unitPrice && unitPrice.display > 0
            ? currencyRatioFromAmounts(unitPrice.base, unitPrice.display)
            : 1;

        if (!resolvedVariantId || !UUID_RE.test(resolvedVariantId)) {
          throw new Error(
            `Could not resolve a valid cart variant for funnel product ${productId}`
          );
        }

        const boundCartToken = authorization?.cartToken ?? cartToken ?? undefined;
        const result = await addToCartMutation.mutateAsync({
          cartToken: boundCartToken,
          productId: resolvedProductId,
          variantId: resolvedVariantId,
          quantity,
          ...(subscription ? { subscription } : {}),
          options: {
            countryCode,
            currency,
            currencyRatio,
            vendor,
            shop,
            ...(persistedFunnelId ? { upsellFunnelId: persistedFunnelId } : {}),
            ...(persistedFirstStepId ? { upsellFirstStepId: persistedFirstStepId } : {}),
            ...(authorization?.funnelOfferToken
              ? { funnelOfferToken: authorization.funnelOfferToken }
              : {}),
          },
        });

        if (result.cartToken) {
          setCartToken(result.cartToken);
        }

        // Pick the line that Carts just appended. Funnel-priced and regular
        // lines may share product/variant identity, so the last matching line
        // is the authoritative result of this mutation.
        const updatedLine = [...(result.cart?.items ?? [])]
          .reverse()
          .find(
            (line) => line.product_id === resolvedProductId && line.variant_id === resolvedVariantId
          );

        let eventId: string | null = null;
        if (offerProduct) {
          const variantForTrack =
            offerProduct.variants.find((v) => v.id === resolvedVariantId) ??
            offerProduct.variants.find((v) => v.enabled) ??
            offerProduct.variants[0];
          const appliedPrice = updatedLine?.price ?? variantForTrack?.price;
          if (variantForTrack && appliedPrice) {
            eventId = trackAddToCart({
              product: {
                uuid: offerProduct.uuid ?? offerProduct.id ?? resolvedProductId,
                title: offerProduct.title,
                slug: offerProduct.slug,
                tags: offerProduct.tags,
                images: offerProduct.images,
              },
              variant: {
                id: variantForTrack.id,
                title: variantForTrack.title,
                sku: variantForTrack.sku,
                price: appliedPrice,
                compare_at_price: updatedLine?.compare_at_price ?? variantForTrack.compare_at_price,
                variation_attributes: variantForTrack.variation_attributes,
              },
              quantity,
              currency,
              cartId: result.cartToken ?? boundCartToken ?? null,
            });
          }
        }

        // The carts BFF response is the authority for post-mutation quantities,
        // totals, line names and prices. It also provides a complete canonical
        // event fallback when catalog enrichment was temporarily unavailable.
        if (!eventId && updatedLine) {
          eventId = trackAddToCartFromLines({
            currency: result.cart?.currency ?? currency,
            cartId: result.cartToken,
            lines: [
              {
                product_id: updatedLine.product_id,
                variant_id: updatedLine.variant_id,
                quantity,
                name: updatedLine.name,
                price: { display: updatedLine.price.display * quantity },
                image_url: updatedLine.image_url,
              },
            ],
          });
        }

        // Advance to the cart step BEFORE navigating. Fire-and-forget left
        // /cart evaluating page_view on the PDP step; lock could also reset
        // when cartToken had not committed yet → no cart page experience.
        const businessEventId = eventId
          ? buildBrowserBusinessEventId(EVENT_TYPES.PRODUCT_ADDED_TO_CART, eventId)
          : undefined;
        await emitCartUpdated({
          pageContext: result.cart
            ? toFunnelCartPageContext(result.cart)
            : {
                pageType: 'cart',
                cartToken: result.cartToken ?? boundCartToken,
                cartProductIds: [resolvedProductId],
                cartItemCount: quantity,
              },
          params: {
            productId: resolvedProductId,
            variantId: resolvedVariantId,
            quantity,
          },
          emitBehavioralResult: item?.semanticResultOwner !== 'dispatcher',
          ...(businessEventId ? { businessEventId } : {}),
        });

        // QA / debug: open the full /cart page so the cart-step funnel page
        // experience mounts (SideCart cannot host that takeover). Preserve
        // funnel_* flags so console debug + secure cart proof stay on.
        if (isFunnelUiEventsDebugEnabled()) {
          const search =
            typeof window !== 'undefined' ? window.location.search : '';
          navigate(buildCartPagePath(search));
        } else {
          showCart();
        }
        return {
          resultParams: {
            productId: resolvedProductId,
            variantId: resolvedVariantId,
            quantity,
            ...(businessEventId ? { businessEventId } : {}),
          },
        };
      } catch (error) {
        storefrontLayoutLogger.error(
          '[useFunnelAddToCart] Failed to add funnel offer to cart',
          error instanceof Error ? error : undefined,
          { productId, variantId, quantity }
        );
        throw error;
      }
    },
    [
      addToCartMutation,
      persistedFunnelId,
      persistedFirstStepId,
      cartToken,
      setCartToken,
      showCart,
      navigate,
      countryCode,
      currency,
      vendor,
      shop,
      emitCartUpdated,
    ]
  );

  return handleFunnelAddToCart;
}

/** Reserves the cart UUID before funnels signs an offer for that exact cart. */
export function usePrepareFunnelCartToken(): () => string {
  const { cartToken, setCartToken } = useCartContext();

  return useCallback(() => {
    if (cartToken) return cartToken;
    const preparedToken = uuidv4();
    setCartToken(preparedToken);
    return preparedToken;
  }, [cartToken, setCartToken]);
}
